import React, { useEffect, useState } from 'react';

import { IProportionBar } from '../../../../interfaces/charts';
import { formatPercentage } from '../../../../utils/utils';
import BdsDivider from '../../Globals/BdsDivider/BdsDivider';
import './ProportionBar.scss';

const MIN_PERCENTAGE_FOR_LEGEND = 15;
const MIN_PERCENTAGE_FOR_TOOLTIP = 10;
const STATIC_MIN_PERCENTAGE_INLINE = 15;
const TOP_INLINE_COUNT_STATIC = 3;

const ProportionBarSegment = ({
  item,
  index,
  isStatic = false
}: {
  item: { label: string; value: number; percentage: number };
  index: number;
  isStatic?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      className={`proportion-bar-segment segment-color-${index} ${isStatic ? 'static' : ''}`}
      style={{ maxWidth: `${item.percentage}%` }}
      onMouseEnter={() => !isStatic && setHovered(true)}
      onMouseLeave={() => !isStatic && setHovered(false)}
    >
      {!isStatic && (
        <>
          {item.percentage < MIN_PERCENTAGE_FOR_LEGEND ? (
            <div
              className={`segment-tooltip-full ${hovered ? 'visible' : ''} ${
                item.percentage < MIN_PERCENTAGE_FOR_TOOLTIP ? 'right' : ''
              }`}
            >
              <div className="tooltip-label">
                <span className={`tooltip-color segment-color-${index}`} /> {item.label}
              </div>
              <div className="tooltip-percentage">{formatPercentage(item.percentage)}</div>
            </div>
          ) : (
            <div className={`segment-tooltip-simple ${hovered ? 'visible' : ''}`}>
              <span className="tooltip-percentage">{formatPercentage(item.percentage)}</span>
            </div>
          )}
        </>
      )}
    </button>
  );
};

const ProportionBar = ({ data, isStatic = false }: IProportionBar) => {
  const [barItems, setBarItems] = useState<{ label: string; value: number; percentage: number }[]>([]);

  useEffect(() => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const itemsWithPercentage = data.map((item) => ({
      ...item,
      percentage: total > 0 ? (item.value / total) * 100 : 0
    }));

    setBarItems(
      [...itemsWithPercentage].sort((a, b) => b.percentage - a.percentage).filter((item) => item.percentage > 0)
    );
  }, [data]);

  const inlineLegendItems = isStatic
    ? barItems.filter((i) => i.percentage >= STATIC_MIN_PERCENTAGE_INLINE).slice(0, TOP_INLINE_COUNT_STATIC)
    : barItems;

  const overflowLegendItems = isStatic ? barItems.filter((i) => !inlineLegendItems.includes(i)) : [];

  return (
    <div className="proportion-bar-container">
      <div className="proportion-bar">
        {barItems.map((item, index) => (
          <ProportionBarSegment key={item.label} item={item} index={index} isStatic={isStatic} />
        ))}
      </div>

      <div className="proportion-bar-legends-content">
        <div className={`proportion-bar-legend ${isStatic ? 'static-inline' : ''}`}>
          {inlineLegendItems.map((item, index) => {
            if (!isStatic && item.percentage < MIN_PERCENTAGE_FOR_LEGEND) return null;
            return (
              <div key={item.label} className="legend-item" style={{ maxWidth: `${item.percentage}%` }}>
                <span className={`legend-color segment-color-${index}`} />
                <span className="legend-label">
                  {isStatic ? (
                    <>
                      {item.label} - <span className="legend-percentage">{formatPercentage(item.percentage)}</span>
                    </>
                  ) : (
                    item.label
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {isStatic && overflowLegendItems.length > 0 && (
          <div className="proportion-bar-overflow-wrapper">
            <div className="overflow-divider-content">
              <BdsDivider size="s" color="high" />
            </div>
            <div className="proportion-bar-legend static-overflow">
              {overflowLegendItems.map((item) => {
                const realIndex = barItems.findIndex((b) => b.label === item.label);
                return (
                  <div key={item.label} className="legend-item legend-overflow-item">
                    <span className={`legend-color segment-color-${realIndex}`} />
                    <span className="legend-label">
                      {item.label} - <span className="legend-percentage">{formatPercentage(item.percentage)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProportionBar;
